import {
  getHubblSSOFilterLabelColors,
  getHubblSSOFilterLabels,
  HubblFilter,
  Palette,
  SSOLocationMetadata,
  Department,
  getRandomPrimaryColor,
} from 'hubbl-shared';
import React from 'react';
import styled from 'styled-components';
import {TextHeading7} from '..';

const PillsContainer = styled.div<{
  maxWidth?: number;
  justifyContent?: 'flex-start' | 'flex-end';
}>`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-height: 32px;
  width: 100%;
  justify-content: ${props => props.justifyContent ?? 'flex-start'};
  max-width: ${props => props.maxWidth}px;
`;

const CustomPill = styled.div<{color: Palette}>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: ${props => props.color};
  color: ${Palette.white};
  border-radius: 12px;
  margin: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  gap: 8px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${Palette.almostBlack};
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  opacity: 0.8;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
    scale: 1.1;
  }
`;

interface SelectedFilterPillsProps {
  filters: HubblFilter[];
  orgMetadata: Record<string, SSOLocationMetadata> | undefined;
  companyDepartments?: Department[];
  removeSelection?: (
    locationId: string,
    subCompanyId?: string,
    departmentId?: string,
  ) => void;
  maxWidth?: number;
  justifyContent?: 'flex-start' | 'flex-end';
}

export const SelectedFilterPills: React.FC<SelectedFilterPillsProps> = ({
  filters,
  orgMetadata,
  companyDepartments,
  removeSelection,
  maxWidth,
  justifyContent,
}) => {
  return (
    <PillsContainer maxWidth={maxWidth} justifyContent={justifyContent}>
      {filters.map((filter, index) => {
        // Handle department filters differently from SSO filters
        const isDepartmentFilter = filter.type === 'department';

        const label = isDepartmentFilter
          ? companyDepartments?.find(d => d.id === filter.id)?.label.en ||
            companyDepartments?.find(d => d.id === filter.id)?.label.da ||
            filter.id
          : getHubblSSOFilterLabels(filter, orgMetadata);

        const color = isDepartmentFilter
          ? getRandomPrimaryColor(filter.id)
          : getHubblSSOFilterLabelColors(filter);

        const parts = filter.id.split('/');
        let locationId = '';
        let subCompanyId = '';
        let departmentId = '';

        parts.forEach(part => {
          if (part.startsWith('loc:')) {
            locationId = part.substring(4);
          } else if (part.startsWith('sub:')) {
            subCompanyId = part.substring(4);
          } else if (part.startsWith('dept:')) {
            departmentId = part.substring(5);
          }
        });

        return (
          <CustomPill
            key={`filter-${index}`}
            color={color ?? Palette.primaryCherokee}>
            <TextHeading7 text={label} />
            {removeSelection && (
              <RemoveButton
                onClick={() => {
                  if (departmentId) {
                    removeSelection(locationId, subCompanyId, departmentId);
                  } else if (subCompanyId) {
                    removeSelection(locationId, subCompanyId);
                  } else {
                    removeSelection(locationId);
                  }
                }}>
                ×
              </RemoveButton>
            )}
          </CustomPill>
        );
      })}
    </PillsContainer>
  );
};
